import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  ArrowLeft,
  Download,
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  Loader2,
  Calendar,
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { SettlementStatus } from '../../models/Accountant';
import {
  getVendorLedgerDetails,
  settleVendorAmount,
  VendorLedgerDetailsResponse,
} from '../../services/MasterAdmin/accountantService';
import {
  AccountantInfoTooltip,
  PAYOUT_POLICY_TOOLTIP,
} from '../../components/Accountant/AccountantInfoTooltip';
import {
  DoctorClinicLabel,
  isDoctorVendorType,
} from '../../components/Accountant/DoctorClinicLabel';
import {
  CommissionBreakdownCell,
  ListPriceCell,
  OfferSummaryBanner,
  PlatformOfferCell,
  SettlementCalcHint,
} from '../../components/Accountant/PlatformOfferCells';
import { aggregateOfferBreakdown } from '../../utils/accountantOfferBreakdown';
import { exportVendorSettlementExcel } from '../../utils/exportVendorSettlementExcel';

type VendorLedgerDetailsPageProps = {
  vendorIdProp?: string;
  embedded?: boolean;
};

const VendorLedgerDetailsPage: React.FC<VendorLedgerDetailsPageProps> = ({
  vendorIdProp,
  embedded = false,
}) => {
  const { vendorId: vendorIdFromParams } = useParams<{ vendorId: string }>();
  const vendorId = vendorIdProp || vendorIdFromParams;
  const navigate = useNavigate();
  const [ledgerEntries, setLedgerEntries] = useState<any[]>([]);
  const [settlementQueue, setSettlementQueue] = useState<any[]>([]);
  const [vendorInfo, setVendorInfo] = useState<any>(null);
  const [totalCharges, setTotalCharges] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [entryTypeFilter, setEntryTypeFilter] = useState<string>('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [showSettleDialog, setShowSettleDialog] = useState(false);
  const [settling, setSettling] = useState(false);
  const [settlingEntryId, setSettlingEntryId] = useState<string | null>(null);
  const [settlementSuccess, setSettlementSuccess] = useState<any>(null);
  const [selectedLedgerIds, setSelectedLedgerIds] = useState<string[]>([]);
  const [settlementTargetIds, setSettlementTargetIds] = useState<string[]>([]);
  const [apiSummary, setApiSummary] = useState({
    pendingSettlement: 0,
    onHold: 0,
    totalEarnings: 0,
    settleableAmount: 0,
    settleableCount: 0,
    waitingT2Amount: 0,
    waitingT2Count: 0,
    canSettle: false,
  });
  const [settleableLedgerIds, setSettleableLedgerIds] = useState<string[]>([]);
  const [offerSummary, setOfferSummary] = useState({
    totalOfferDiscount: 0,
    totalBaseCommission: 0,
    totalPlatformCommission: 0,
    totalOfferAbsorbed: 0,
    offerEntryCount: 0,
  });
  const [payoutPolicy, setPayoutPolicy] = useState({
    label: 'T+2',
    holdDays: 2,
    description: 'Entries are settleable 2 calendar days after service completion',
  });
  const [exporting, setExporting] = useState(false);

  // Fetch ledger entries
  const fetchLedgerEntries = useCallback(async () => {
    if (!vendorId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await getVendorLedgerDetails(vendorId, {
        status: statusFilter !== 'ALL' ? statusFilter.toLowerCase() : undefined,
        entryType: entryTypeFilter !== 'ALL' ? entryTypeFilter : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        page: currentPage,
        limit: 50,
      });

      let entries = response.data.ledgerEntries;

      // Apply client-side search filter
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        entries = entries.filter(
          (entry: any) =>
            entry.serviceReferenceId?.toLowerCase().includes(searchLower) ||
            entry.serviceType?.toLowerCase().includes(searchLower) ||
            entry.ledgerId?.toLowerCase().includes(searchLower)
        );
      }

      setLedgerEntries(entries);
      setSettlementQueue(response.data.settlementQueue || []);
      setVendorInfo(response.data.vendor);
      setPayoutPolicy(
        response.data.payoutPolicy || {
          label: 'T+2',
          holdDays: 2,
          description: 'Entries are settleable 2 calendar days after service completion',
        }
      );
      setApiSummary({
        pendingSettlement: Number(response.data.pendingSettlement ?? 0),
        onHold: Number(response.data.onHold ?? 0),
        totalEarnings: Number(response.data.totalEarnings ?? 0),
        settleableAmount: Number(response.data.settleableAmount ?? 0),
        settleableCount: Number(response.data.settleableCount ?? 0),
        waitingT2Amount: Number(response.data.waitingT2Amount ?? 0),
        waitingT2Count: Number(response.data.waitingT2Count ?? 0),
        canSettle: response.data.canSettle ?? Number(response.data.settleableAmount ?? 0) > 0,
      });
      setOfferSummary(
        response.data.offerSummary || {
          totalOfferDiscount: 0,
          totalBaseCommission: 0,
          totalPlatformCommission: 0,
          totalOfferAbsorbed: 0,
          offerEntryCount: 0,
        }
      );
      setSettleableLedgerIds(response.data.settleableLedgerIds || []);
      setSelectedLedgerIds((prev) =>
        prev.filter((id) =>
          (response.data.settlementQueue || []).some((entry: any) => entry.ledgerId === id)
        )
      );
      // Get totalCharges from API response (matches vendor dashboard API)
      const chargesValue = response.data.totalCharges;
      setTotalCharges(chargesValue || 0);
      setTotalPages(response.data.pagination.totalPages);
      setTotalEntries(response.data.pagination.total);
    } catch (err: any) {
      console.error('Error fetching vendor ledger details:', err);
      setError(err.response?.data?.message || 'Failed to fetch ledger details. Please try again.');
      setLedgerEntries([]);
    } finally {
      setLoading(false);
    }
  }, [vendorId, statusFilter, entryTypeFilter, startDate, endDate, currentPage, searchTerm]);

  useEffect(() => {
    fetchLedgerEntries();
  }, [fetchLedgerEntries]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [statusFilter, entryTypeFilter, startDate, endDate, searchTerm]);

  const handleExportExcel = useCallback(async () => {
    if (!vendorId || exporting) return;

    try {
      setExporting(true);
      const pageLimit = 200;
      let page = 1;
      let totalPagesForExport = 1;
      const allEntries: any[] = [];
      let exportVendor = vendorInfo;
      let exportQueue = settlementQueue;
      let exportSummary = {
        totalNetPayable: apiSummary.totalEarnings,
        pendingSettlement: apiSummary.pendingSettlement,
        onHold: apiSummary.onHold,
        settleableAmount: apiSummary.settleableAmount,
        settleableCount: apiSummary.settleableCount,
        waitingT2Amount: apiSummary.waitingT2Amount,
        waitingT2Count: apiSummary.waitingT2Count,
        totalCharges,
        totalOfferDiscount: offerSummary.totalOfferDiscount,
        totalPlatformCommission: offerSummary.totalPlatformCommission,
        totalBaseCommission: offerSummary.totalBaseCommission,
        totalOfferAbsorbed: offerSummary.totalOfferAbsorbed,
        payoutPolicyLabel: payoutPolicy.label,
      };

      do {
        const response = await getVendorLedgerDetails(vendorId, {
          status: statusFilter !== 'ALL' ? statusFilter.toLowerCase() : undefined,
          entryType: entryTypeFilter !== 'ALL' ? entryTypeFilter : undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          page,
          limit: pageLimit,
        });

        let pageEntries = response.data.ledgerEntries || [];
        if (searchTerm.trim()) {
          const searchLower = searchTerm.toLowerCase();
          pageEntries = pageEntries.filter(
            (entry: any) =>
              entry.serviceReferenceId?.toLowerCase().includes(searchLower) ||
              entry.serviceType?.toLowerCase().includes(searchLower) ||
              entry.ledgerId?.toLowerCase().includes(searchLower)
          );
        }

        allEntries.push(...pageEntries);
        exportVendor = response.data.vendor || exportVendor;
        exportQueue = response.data.settlementQueue || exportQueue;
        exportSummary = {
          totalNetPayable: Number(response.data.totalEarnings ?? exportSummary.totalNetPayable),
          pendingSettlement: Number(response.data.pendingSettlement ?? exportSummary.pendingSettlement),
          onHold: Number(response.data.onHold ?? exportSummary.onHold),
          settleableAmount: Number(response.data.settleableAmount ?? exportSummary.settleableAmount),
          settleableCount: Number(response.data.settleableCount ?? exportSummary.settleableCount),
          waitingT2Amount: Number(response.data.waitingT2Amount ?? exportSummary.waitingT2Amount),
          waitingT2Count: Number(response.data.waitingT2Count ?? exportSummary.waitingT2Count),
          totalCharges: Number(response.data.totalCharges ?? exportSummary.totalCharges),
          totalOfferDiscount: Number(
            response.data.offerSummary?.totalOfferDiscount ?? exportSummary.totalOfferDiscount
          ),
          totalPlatformCommission: Number(
            response.data.offerSummary?.totalPlatformCommission ??
              exportSummary.totalPlatformCommission
          ),
          totalBaseCommission: Number(
            response.data.offerSummary?.totalBaseCommission ?? exportSummary.totalBaseCommission
          ),
          totalOfferAbsorbed: Number(
            response.data.offerSummary?.totalOfferAbsorbed ?? exportSummary.totalOfferAbsorbed
          ),
          payoutPolicyLabel:
            response.data.payoutPolicy?.label || exportSummary.payoutPolicyLabel || 'T+2',
        };
        totalPagesForExport = response.data.pagination?.totalPages || 1;
        page += 1;
      } while (page <= totalPagesForExport);

      const result = exportVendorSettlementExcel({
        vendor: exportVendor,
        summary: exportSummary,
        ledgerEntries: allEntries,
        settlementQueue: exportQueue,
      });

      toast.success(
        `Exported ${result.ledgerRows} ledger ${result.ledgerRows === 1 ? 'row' : 'rows'} to ${result.fileName}`
      );
    } catch (err: any) {
      console.error('Error exporting vendor settlement Excel:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to export Excel. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [
    vendorId,
    exporting,
    vendorInfo,
    settlementQueue,
    apiSummary,
    totalCharges,
    offerSummary,
    payoutPolicy.label,
    statusFilter,
    entryTypeFilter,
    startDate,
    endDate,
    searchTerm,
  ]);

  const formatCurrency = (amount: number) => {
    const value = Number(amount) || 0;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const amountColorClass = (amount: number, positiveClass: string, negativeClass = 'text-red-600 dark:text-red-400') => {
    const value = Number(amount) || 0;
    if (value < 0) return negativeClass;
    if (value === 0) return 'text-gray-500 dark:text-gray-400';
    return positiveClass;
  };

  const getStatusBadge = (status: SettlementStatus) => {
    const statusConfig = {
      PENDING: {
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
        icon: Clock,
      },
      PROCESSING: {
        color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
        icon: Clock,
      },
      SETTLED: {
        color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        icon: CheckCircle2,
      },
      ON_HOLD: {
        color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
        icon: AlertCircle,
      },
      FAILED: {
        color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
        icon: XCircle,
      },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.replace('_', ' ')}
      </span>
    );
  };

  const resolveServiceStatus = (entry: any): string => {
    const candidate =
      entry?.lifecycleStatus ||
      entry?.lifecycle_status ||
      entry?.metadata?.lifecycleStatus ||
      entry?.metadata?.appointmentStatus ||
      entry?.metadata?.bookingStatus ||
      entry?.metadata?.orderStatus ||
      entry?.metadata?.serviceStatus ||
      null;
    return candidate ? String(candidate).toUpperCase() : 'N/A';
  };

  const getServiceStatusBadge = (status: string) => {
    const normalized = String(status || 'N/A').toUpperCase();
    let bgColor = 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    if (
      normalized === 'COMPLETED' ||
      normalized === 'DELIVERED' ||
      normalized === 'SETTLED'
    ) {
      bgColor = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    } else if (
      normalized === 'PENDING_SERVICE' ||
      normalized === 'PENDING' ||
      normalized === 'CONFIRMED' ||
      normalized === 'IN_PROGRESS'
    ) {
      bgColor = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    } else if (normalized === 'CANCELLED' || normalized === 'REJECTED') {
      bgColor = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    }
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${bgColor}`}>
        {normalized.replace(/_/g, ' ')}
      </span>
    );
  };

  // Calculate dashboard stats
  const dashboardStats = React.useMemo(() => {
    const offerTotals = aggregateOfferBreakdown(ledgerEntries);
    const stats = {
      totalEarnings: 0,
      totalCommission: 0,
      totalGstOnCommission: 0,
      totalRefunds: 0,
      totalGatewayFees: totalCharges || 0,
      totalNetPayable: 0,
      pendingAmount: 0,
      settledAmount: 0,
      onHoldAmount: 0,
      transactionCount: ledgerEntries.length,
      totalOfferDiscount: offerTotals.totalOfferDiscount,
      totalVendorCommission: offerTotals.totalVendorCommission,
      totalOfferAbsorbed: offerTotals.totalOfferAbsorbed,
      offerEntryCount: offerTotals.offerEntryCount,
    };

    ledgerEntries.forEach((entry) => {
      const netPayable = Number(entry.netPayable) || 0;
      const refundAmount = Number(entry.refundAmount) || 0;
      stats.totalEarnings += Number(entry.grossAmount) || 0;
      stats.totalCommission += Number(entry.commissionAmount) || 0;
      stats.totalGstOnCommission += Number(entry.gstOnCommission) || 0;
      stats.totalRefunds += refundAmount;
      stats.totalNetPayable += netPayable;

      if (entry.status === 'pending') {
        stats.pendingAmount += netPayable;
      } else if (entry.status === 'settled') {
        stats.settledAmount += netPayable;
      } else if (entry.status === 'on_hold') {
        stats.onHoldAmount += netPayable;
      }
    });

    return stats;
  }, [ledgerEntries, totalCharges]);

  const selectedEntries = React.useMemo(
    () => settlementQueue.filter((entry) => selectedLedgerIds.includes(entry.ledgerId)),
    [settlementQueue, selectedLedgerIds]
  );

  const isAllSettleableSelected =
    settleableLedgerIds.length > 0 &&
    selectedLedgerIds.length === settleableLedgerIds.length &&
    settleableLedgerIds.every((id) => selectedLedgerIds.includes(id));

  const selectedSettlementAmount = React.useMemo(() => {
    if (isAllSettleableSelected) {
      return apiSummary.settleableAmount;
    }
    return selectedEntries.reduce((sum, entry) => sum + (Number(entry.netPayable) || 0), 0);
  }, [isAllSettleableSelected, apiSummary.settleableAmount, selectedEntries]);

  const settlementDialogAmount = React.useMemo(() => {
    const ids = settlementTargetIds.length > 0 ? settlementTargetIds : selectedLedgerIds;
    if (
      settleableLedgerIds.length > 0 &&
      ids.length === settleableLedgerIds.length &&
      settleableLedgerIds.every((id) => ids.includes(id))
    ) {
      return apiSummary.settleableAmount;
    }
    return settlementQueue
      .filter((entry) => ids.includes(entry.ledgerId))
      .reduce((sum, entry) => sum + (Number(entry.netPayable) || 0), 0);
  }, [
    settlementQueue,
    selectedLedgerIds,
    settlementTargetIds,
    settleableLedgerIds,
    apiSummary.settleableAmount,
  ]);

  const toggleLedgerSelection = (ledgerId: string) => {
    setSelectedLedgerIds((prev) =>
      prev.includes(ledgerId) ? prev.filter((id) => id !== ledgerId) : [...prev, ledgerId]
    );
  };

  const selectAllEligible = () => {
    setSelectedLedgerIds(settleableLedgerIds);
  };

  const clearSelection = () => {
    setSelectedLedgerIds([]);
  };

  const openSettlementDialog = (ledgerIds: string[]) => {
    setSettlementTargetIds(ledgerIds);
    setSettlementSuccess(null);
    setShowSettleDialog(true);
  };

  const handleSettleEntries = async (ledgerIds: string[]) => {
    if (!vendorId || ledgerIds.length === 0) return false;
    try {
      setSettling(true);
      const response = await settleVendorAmount(vendorId, { ledgerIds });
      setSettlementSuccess(response.data);
      setSelectedLedgerIds([]);
      setSettlementTargetIds([]);
      return true;
    } catch (error: any) {
      console.error('Error settling vendor:', error);
      setSettlementSuccess({
        error: true,
        message: error.response?.data?.message || 'Failed to process settlement. Please try again.',
      });
      return false;
    } finally {
      setSettling(false);
      setSettlingEntryId(null);
    }
  };

  const handleSettleSingleEntry = async (ledgerId: string) => {
    setSettlingEntryId(ledgerId);
    setSettlementSuccess(null);
    const ok = await handleSettleEntries([ledgerId]);
    if (ok) {
      await fetchLedgerEntries();
    }
  };

  const displayPending = apiSummary.pendingSettlement;
  const displayOnHold = apiSummary.onHold;
  const displayTotalEarnings = apiSummary.totalEarnings;
  const displaySettleable = apiSummary.settleableAmount;
  const displayWaitingT2 = apiSummary.waitingT2Amount;
  const canSettle = apiSummary.canSettle;
  const isDoctorVendor = isDoctorVendorType(vendorInfo?.vendorType);
  const ledgerTableColSpan = isDoctorVendor ? 14 : 13;
  const queueTableColSpan = isDoctorVendor ? 8 : 7;

  const renderDoctorClinicCell = () => (
    <DoctorClinicLabel
      clinicName={vendorInfo?.clinicName}
      doctorName={vendorInfo?.doctorName}
      fallbackName={vendorInfo?.vendorName}
    />
  );

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
          {!embedded && (
            <button
              onClick={() => navigate('/accountant/settlements')}
              className="shrink-0 rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="min-w-0">
            {embedded ? (
              <h2 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">Settlement</h2>
            ) : isDoctorVendor ? (
              <>
                <DoctorClinicLabel
                  clinicName={vendorInfo?.clinicName}
                  doctorName={vendorInfo?.doctorName}
                  fallbackName={vendorInfo?.vendorName}
                  clinicClassName="truncate text-xl font-bold text-gray-900 dark:text-white sm:text-2xl lg:text-3xl"
                  doctorClassName="mt-1 truncate text-sm text-gray-600 dark:text-gray-400"
                />
                <p className="mt-1 text-gray-600 dark:text-gray-400">
                  {vendorInfo?.vendorType?.replace(/_/g, ' ')}
                </p>
              </>
            ) : (
              <>
                <h2 className="truncate text-xl font-bold text-gray-900 dark:text-white sm:text-2xl lg:text-3xl">
                  {vendorInfo?.vendorName || 'Vendor'}
                </h2>
                <p className="mt-1 text-gray-600 dark:text-gray-400">
                  {vendorInfo?.vendorType?.replace(/_/g, ' ')}
                </p>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
          <button
            type="button"
            onClick={handleExportExcel}
            disabled={exporting || loading || !vendorId}
            className="flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            <span>{exporting ? 'Exporting…' : 'Export'}</span>
          </button>
          {canSettle && (
            <button
              onClick={() => openSettlementDialog(selectedLedgerIds)}
              disabled={selectedLedgerIds.length === 0}
              className="flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>
                Settle Selected{selectedLedgerIds.length > 0 ? ` (${selectedLedgerIds.length})` : ''}
              </span>
            </button>
          )}
          {!canSettle && apiSummary.waitingT2Count > 0 && (
              <button
                disabled
                title={`No ${payoutPolicy.label} eligible entries yet`}
                className="flex cursor-not-allowed items-center gap-2 rounded-lg bg-gray-300 px-4 py-2 text-gray-500 dark:bg-gray-600 dark:text-gray-400"
              >
                <Clock className="w-4 h-4" />
                <span>Waiting for T+2</span>
              </button>
            )}
        </div>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
            Payout policy: {payoutPolicy.label}
          </p>
          <AccountantInfoTooltip
            content={PAYOUT_POLICY_TOOLTIP}
            label="Payout policy details"
            iconClassName="text-blue-700/70 hover:text-blue-900 dark:text-blue-400/80 dark:hover:text-blue-200"
          />
        </div>
      </div>

      <OfferSummaryBanner
        totalOfferDiscount={offerSummary.totalOfferDiscount || dashboardStats.totalOfferDiscount}
        totalOfferAbsorbed={offerSummary.totalOfferAbsorbed || dashboardStats.totalOfferAbsorbed}
        totalVendorCommission={offerSummary.totalBaseCommission || dashboardStats.totalVendorCommission}
        totalPlatformCommission={offerSummary.totalPlatformCommission || dashboardStats.totalCommission}
        offerEntryCount={offerSummary.offerEntryCount || dashboardStats.offerEntryCount}
      />

      {/* Dashboard Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Net Payable</p>
          <p className={`text-2xl font-bold mt-1 ${amountColorClass(displayTotalEarnings, 'text-gray-900 dark:text-white')}`}>
            {formatCurrency(displayTotalEarnings)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Sum of vendor payouts (all entries)</p>
          {dashboardStats.totalOfferDiscount > 0 && (
            <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-1">
              Offers: −{formatCurrency(dashboardStats.totalOfferDiscount)}
            </p>
          )}
          {displayTotalEarnings < 0 && (
            <p className="text-xs text-red-500 dark:text-red-400 mt-1">Vendor owes platform (net)</p>
          )}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Platform Commission</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {formatCurrency(offerSummary.totalPlatformCommission || dashboardStats.totalCommission)}
          </p>
          {(offerSummary.totalBaseCommission || dashboardStats.totalVendorCommission) >
            (offerSummary.totalPlatformCommission || dashboardStats.totalCommission) + 0.009 && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Gross commission before offer:{' '}
              {formatCurrency(offerSummary.totalBaseCommission || dashboardStats.totalVendorCommission)}
            </p>
          )}
          {dashboardStats.totalGstOnCommission > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              GST on Commission: {formatCurrency(dashboardStats.totalGstOnCommission)}
            </p>
          )}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Charges Recovered</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {formatCurrency(dashboardStats.totalGatewayFees)}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Transactions</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {dashboardStats.transactionCount}
          </p>
        </div>
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm text-gray-600 dark:text-gray-400">T+2 Settleable</p>
          <p className={`mt-1 text-xl font-bold ${amountColorClass(displaySettleable, 'text-green-600 dark:text-green-400')}`}>
            {formatCurrency(displaySettleable)}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {apiSummary.settleableCount} {apiSummary.settleableCount === 1 ? 'entry' : 'entries'}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <p className="text-sm text-gray-600 dark:text-gray-400">Waiting for T+2</p>
          <p className={`mt-1 text-xl font-bold ${amountColorClass(displayWaitingT2, 'text-amber-600 dark:text-amber-400')}`}>
            {formatCurrency(displayWaitingT2)}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {apiSummary.waitingT2Count} {apiSummary.waitingT2Count === 1 ? 'entry' : 'entries'}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Pending Amount</p>
          <p className={`text-xl font-bold mt-1 ${amountColorClass(displayPending, 'text-yellow-600 dark:text-yellow-400')}`}>
            {formatCurrency(displayPending)}
          </p>
          {displayPending < 0 && (
            <p className="text-xs text-red-500 dark:text-red-400 mt-1">Vendor owes platform</p>
          )}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Settled Amount</p>
          <p className={`text-xl font-bold mt-1 ${amountColorClass(dashboardStats.settledAmount, 'text-green-600 dark:text-green-400')}`}>
            {formatCurrency(dashboardStats.settledAmount)}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">On Hold Amount</p>
          <p className={`text-xl font-bold mt-1 ${amountColorClass(displayOnHold, 'text-orange-600 dark:text-orange-400')}`}>
            {formatCurrency(displayOnHold)}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Refund Amount</p>
          <p className="text-xl font-bold text-red-600 dark:text-red-400 mt-1">
            {formatCurrency(dashboardStats.totalRefunds)}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by service ref..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="ALL">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="SETTLED">Settled</option>
            <option value="ON_HOLD">On Hold</option>
          </select>
          <select
            value={entryTypeFilter}
            onChange={(e) => {
              setEntryTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="ALL">All Types</option>
            <option value="NORMAL">Normal</option>
            <option value="ADJUSTMENT">Adjustment</option>
          </select>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Start Date"
            />
          </div>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="End Date"
            />
          </div>
        </div>
      </div>

      {/* T+2 Settlement Queue — only eligible entries */}
      <div className="overflow-hidden rounded-lg border border-green-200 bg-white shadow-sm dark:border-green-800 dark:bg-gray-800">
        <div className="border-b border-green-200 bg-green-50 px-4 py-3 dark:border-green-800 dark:bg-green-900/20 sm:px-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Settlement Queue ({payoutPolicy.label})
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {settlementQueue.length} {settlementQueue.length === 1 ? 'entry' : 'entries'} ready ·{' '}
                {formatCurrency(displaySettleable)}
              </p>
            </div>
            {settlementQueue.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={selectAllEligible}
                  className="rounded-lg border border-primary-300 px-3 py-1.5 text-sm text-primary-700 transition-colors hover:bg-primary-50 dark:border-primary-700 dark:text-primary-300 dark:hover:bg-primary-900/20"
                >
                  Select all ({settlementQueue.length})
                </button>
                <button
                  type="button"
                  onClick={clearSelection}
                  disabled={selectedLedgerIds.length === 0}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          {selectedLedgerIds.length > 0 && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {selectedLedgerIds.length} selected · {formatCurrency(selectedSettlementAmount)}
            </p>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <span className="sr-only">Select</span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Service Ref
                </th>
                {isDoctorVendor && (
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Clinic / Doctor
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Net Payable
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {loading ? (
                <tr>
                  <td colSpan={queueTableColSpan} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    Loading settlement queue...
                  </td>
                </tr>
              ) : settlementQueue.length === 0 ? (
                <tr>
                  <td colSpan={queueTableColSpan} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No T+2 eligible entries right now. Recent entries will appear here after {payoutPolicy.holdDays} days.
                  </td>
                </tr>
              ) : (
                settlementQueue.map((entry) => {
                  const netPayable = Number(entry.netPayable) || 0;
                  return (
                    <tr key={entry.ledgerId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedLedgerIds.includes(entry.ledgerId)}
                          onChange={() => toggleLedgerSelection(entry.ledgerId)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          aria-label={`Select ${entry.serviceReferenceId}`}
                        />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {(entry.serviceCompletedAt || entry.createdAt)
                          ? new Date(entry.serviceCompletedAt || entry.createdAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })
                          : '-'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {entry.serviceReferenceId}
                      </td>
                      {isDoctorVendor && (
                        <td className="whitespace-nowrap px-4 py-3">
                          {renderDoctorClinicCell()}
                        </td>
                      )}
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {entry.entryType}
                      </td>
                      <td className={`whitespace-nowrap px-4 py-3 text-sm font-semibold ${amountColorClass(
                        netPayable,
                        'text-green-600 dark:text-green-400'
                      )}`}>
                        {formatCurrency(netPayable)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {getStatusBadge(entry.status.toUpperCase() as SettlementStatus)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <button
                          type="button"
                          onClick={() => void handleSettleSingleEntry(entry.ledgerId)}
                          disabled={settling || settlingEntryId === entry.ledgerId}
                          className="inline-flex items-center gap-1 rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {settlingEntryId === entry.ledgerId ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          )}
                          Settle
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* All Ledger Entries */}
      <div>
        <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">All Ledger Entries</h3>
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Service Ref
                </th>
                {isDoctorVendor && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Clinic / Doctor
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Gross
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  List Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Platform Offer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Refund
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Commission
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  GST on Commission
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Gateway Fee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Total Payable Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Settlement Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Service Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                    <td colSpan={ledgerTableColSpan} className="px-6 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-600 dark:text-primary-400" />
                    <p className="text-gray-500 dark:text-gray-400 mt-4">Loading ledger entries...</p>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                    <td colSpan={ledgerTableColSpan} className="px-6 py-12 text-center">
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 inline-block">
                      <div className="flex items-center space-x-2">
                        <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                        <p className="text-red-800 dark:text-red-300">{error}</p>
                      </div>
                      <button
                        onClick={fetchLedgerEntries}
                        className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        Retry
                      </button>
                    </div>
                  </td>
                </tr>
              ) : ledgerEntries.length === 0 ? (
                <tr>
                    <td colSpan={ledgerTableColSpan} className="px-6 py-12 text-center">
                    <p className="text-gray-500 dark:text-gray-400">No ledger entries found.</p>
                  </td>
                </tr>
              ) : (
                ledgerEntries.map((entry) => {
                  const netPayable = Number(entry.netPayable) || 0;
                  const refundAmount = Number(entry.refundAmount) || 0;
                  const serviceStatus = resolveServiceStatus(entry);

                  return (
                  <tr key={entry.ledgerId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {(entry.serviceCompletedAt || entry.createdAt)
                        ? new Date(entry.serviceCompletedAt || entry.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {entry.serviceReferenceId}
                    </td>
                    {isDoctorVendor && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderDoctorClinicCell()}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <span className={`px-2 py-1 rounded text-xs w-fit ${
                        entry.entryType === 'ADJUSTMENT'
                          ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                      }`}>
                        {entry.entryType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatCurrency(Number(entry.grossAmount) || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <ListPriceCell entry={entry} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <PlatformOfferCell entry={entry} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400">
                      {refundAmount !== 0 ? formatCurrency(refundAmount) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {vendorInfo?.vendorType === 'DELIVERY_PARTNER' ? (
                        <span className="text-gray-500 dark:text-gray-400 italic">NA</span>
                      ) : (
                        <CommissionBreakdownCell entry={entry} />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {vendorInfo?.vendorType === 'DELIVERY_PARTNER' ? (
                        <span className="text-gray-500 dark:text-gray-400 italic">NA</span>
                      ) : (
                        formatCurrency(Number(entry.gstOnCommission) || 0)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {Number(entry.gatewayFeeRecovered) > 0 ? formatCurrency(Number(entry.gatewayFeeRecovered)) : '-'}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${amountColorClass(
                      netPayable,
                      entry.status === 'pending'
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : entry.status === 'settled'
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-500 dark:text-gray-400'
                    )}`}>
                      <div>{formatCurrency(netPayable)}</div>
                      {netPayable < 0 && (
                        <span className="text-xs font-normal text-red-500 dark:text-red-400">(owes)</span>
                      )}
                      <SettlementCalcHint entry={entry} className="mt-1 max-w-xs font-normal" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(entry.status.toUpperCase() as SettlementStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getServiceStatusBadge(serviceStatus)}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        </div>
      </div>

      {/* Pagination */}
      {!loading && !error && totalPages > 1 && (
        <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing page {currentPage} of {totalPages} ({totalEntries} total entries)
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Settlement Confirmation Dialog */}
      {showSettleDialog && vendorInfo && (
        <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center sm:p-6">
          <div
            className="absolute inset-0 bg-gray-500/75 transition-opacity"
            onClick={() => setShowSettleDialog(false)}
            aria-hidden
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="settlement-dialog-title"
            className="relative flex w-full max-w-lg flex-col overflow-hidden rounded-lg bg-white text-left shadow-xl dark:bg-gray-800 md:max-w-4xl lg:max-w-5xl"
          >
            <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
              <h3 id="settlement-dialog-title" className="text-lg font-medium text-gray-900 dark:text-white">
                Confirm Settlement
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Review settlement details before processing
              </p>
            </div>

            <div className="px-6 py-4">
              {settlementSuccess && !settlementSuccess.error ? (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start">
                    <CheckCircle2 className="h-8 w-8 shrink-0 text-green-600 dark:text-green-400" />
                    <div className="min-w-0 flex-1">
                      <h4 className="mb-3 text-lg font-medium text-green-800 dark:text-green-300">
                        Settlement Processed Successfully!
                      </h4>
                      <div className="grid grid-cols-1 gap-3 text-sm text-green-700 dark:text-green-400 sm:grid-cols-2 lg:grid-cols-3">
                        <p>
                          <span className="font-medium">Vendor:</span> {settlementSuccess.vendorName}
                        </p>
                        <p>
                          <span className="font-medium">Amount Settled:</span>{' '}
                          {formatCurrency(settlementSuccess.totalAmount)}
                        </p>
                        <p>
                          <span className="font-medium">Entries Processed:</span> {settlementSuccess.entryCount}
                        </p>
                        <p className="break-all sm:col-span-2 lg:col-span-2">
                          <span className="font-medium">Settlement Reference:</span>{' '}
                          {settlementSuccess.settlementRef}
                        </p>
                        <p>
                          <span className="font-medium">Settled At:</span>{' '}
                          {new Date(settlementSuccess.settledAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : settlementSuccess && settlementSuccess.error ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start">
                    <XCircle className="h-8 w-8 shrink-0 text-red-600 dark:text-red-400" />
                    <div>
                      <h4 className="text-lg font-medium text-red-800 dark:text-red-300">
                        Settlement Failed
                      </h4>
                      <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                        {settlementSuccess.message}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {(settlementTargetIds.length > 0 ? settlementTargetIds : selectedLedgerIds).length === 0 && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                      <p className="text-sm text-red-800 dark:text-red-300">
                        Select at least one T+2 eligible entry to settle.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/30">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">Vendor Details</p>
                      <div className="mt-3 space-y-2">
                        <p className="text-sm text-gray-900 dark:text-white">
                          <span className="font-medium text-gray-600 dark:text-gray-400">Name:</span>{' '}
                          {vendorInfo.vendorName}
                        </p>
                        <p className="text-sm text-gray-900 dark:text-white">
                          <span className="font-medium text-gray-600 dark:text-gray-400">Type:</span>{' '}
                          {vendorInfo.vendorType?.replace(/_/g, ' ')}
                        </p>
                        <p className="text-sm text-gray-900 dark:text-white">
                          <span className="font-medium text-gray-600 dark:text-gray-400">Policy:</span>{' '}
                          {payoutPolicy.label}
                        </p>
                      </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/30">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">Settlement Summary</p>
                      <div className="mt-3 space-y-3">
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Selected entries</span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {(settlementTargetIds.length > 0 ? settlementTargetIds : selectedLedgerIds).length}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span className="text-sm text-gray-600 dark:text-gray-400">T+2 eligible total</span>
                          <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                            {apiSummary.settleableCount}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-4 border-t border-gray-200 pt-3 dark:border-gray-600">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Vendor settleable</span>
                          <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                            {formatCurrency(displaySettleable)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">Amount to settle</p>
                      <p
                        className={`mt-3 text-3xl font-bold ${
                          settlementDialogAmount < 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-green-700 dark:text-green-400'
                        }`}
                      >
                        {formatCurrency(settlementDialogAmount)}
                      </p>
                      <p className="mt-3 text-xs text-yellow-800 dark:text-yellow-300">
                        Only {payoutPolicy.label} eligible entries will be marked settled. Confirm the bank transfer
                        before proceeding.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-700/50 sm:flex-row sm:justify-end sm:gap-3">
                {settlementSuccess && !settlementSuccess.error ? (
                  <button
                    onClick={() => {
                      setShowSettleDialog(false);
                      setSettlementSuccess(null);
                      // Refresh ledger entries
                      fetchLedgerEntries();
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Close</span>
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setShowSettleDialog(false);
                        setSettlementSuccess(null);
                        setSettlementTargetIds([]);
                      }}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                      disabled={settling}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        const ids =
                          settlementTargetIds.length > 0 ? settlementTargetIds : selectedLedgerIds;
                        const ok = await handleSettleEntries(ids);
                        if (ok) {
                          await fetchLedgerEntries();
                        }
                      }}
                      disabled={
                        settling ||
                        (settlementTargetIds.length === 0 && selectedLedgerIds.length === 0) ||
                        settlementDialogAmount <= 0
                      }
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {settling ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Confirm Settlement</span>
                        </>
                      )}
                    </button>
                  </>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorLedgerDetailsPage;
